import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { FolderOpen, UploadSimple, Trash, DownloadSimple, File, FilePdf, FileXls, FileDoc, FileImage, FileZip, PaperPlaneTilt, EnvelopeSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CATEGORIES = [
  'Fattura',
  'Comunicazione',
  'Report',
  'Modello Fiscale',
  'Contratto',
  'Altro',
];

function fileIcon(name) {
  const ext = (name || '').split('.').pop().toLowerCase();
  if (['pdf'].includes(ext)) return <FilePdf size={20} className="text-red-500" />;
  if (['xls', 'xlsx', 'csv'].includes(ext)) return <FileXls size={20} className="text-green-600" />;
  if (['doc', 'docx'].includes(ext)) return <FileDoc size={20} className="text-blue-600" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <FileImage size={20} className="text-purple-500" />;
  if (['zip', 'rar', '7z'].includes(ext)) return <FileZip size={20} className="text-amber-600" />;
  return <File size={20} className="text-[#52525B]" />;
}

function formatSize(bytes) {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function DocumentsSection({ teachers }) {
  const [teacherId, setTeacherId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyData, setNotifyData] = useState(null);
  const [sendingNotify, setSendingNotify] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (!teacherId) return;
    setLoading(true);
    try {
      const res = await api.getTeacherDocuments(teacherId);
      setDocuments(res.data);
    } catch { toast.error('Errore caricamento documenti'); }
    finally { setLoading(false); }
  }, [teacherId]);

  useEffect(() => { loadDocuments(); }, [loadDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length || !teacherId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('category', uploadCategory);
      formData.append('description', uploadDescription);
      uploadFiles.forEach(f => formData.append('files', f));
      const res = await api.uploadTeacherDocument(teacherId, formData);
      toast.success(`${res.data.uploaded} file caricati`);
      const fileNames = res.data.files.map(f => f.original_name);
      setShowUploadModal(false);
      loadDocuments();
      // Check if teacher has email — if so, offer notification
      const t = teachers.find(x => x.id === teacherId);
      if (t?.email) {
        setNotifyData({ fileNames, category: uploadCategory, description: uploadDescription, teacherName: t.name, teacherEmail: t.email });
        setShowNotifyModal(true);
      }
      setUploadFiles([]);
      setUploadCategory('');
      setUploadDescription('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore nel caricamento'); }
    finally { setUploading(false); }
  };

  const handleSendNotify = async () => {
    setSendingNotify(true);
    try {
      await api.notifyTeacherDocuments(teacherId, {
        file_names: notifyData.fileNames,
        category: notifyData.category,
        description: notifyData.description,
      });
      toast.success(`Notifica inviata a ${notifyData.teacherEmail}`);
      setShowNotifyModal(false);
      setNotifyData(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore invio notifica'); }
    finally { setSendingNotify(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Eliminare questo documento?')) return;
    try {
      await api.deleteTeacherDocument(teacherId, docId);
      toast.success('Documento eliminato');
      loadDocuments();
    } catch { toast.error('Errore eliminazione'); }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await api.downloadAdminDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };

  const teacher = teachers.find(t => t.id === teacherId);

  const categoryColors = {
    'Fattura': 'bg-blue-100 text-blue-700',
    'Comunicazione': 'bg-green-100 text-green-700',
    'Report': 'bg-purple-100 text-purple-700',
    'Modello Fiscale': 'bg-amber-100 text-amber-700',
    'Contratto': 'bg-red-100 text-red-700',
    'Altro': 'bg-gray-100 text-gray-600',
  };

  return (
    <>
      <div className="mb-6 animate-fade-in">
        <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Documenti Insegnanti</h1>
        <p className="text-[#52525B] text-sm">Carica e gestisci fatture, comunicazioni, report e modelli fiscali per ogni insegnante</p>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 animate-fade-in animate-delay-1">
        <Select value={teacherId} onValueChange={setTeacherId}>
          <SelectTrigger className="w-full sm:w-[300px] rounded-sm h-10 text-sm bg-[#0A0A0A] text-white border-[#0A0A0A] font-medium [&>svg]:text-white" data-testid="docs-teacher-select">
            <SelectValue placeholder="Seleziona insegnante..." />
          </SelectTrigger>
          <SelectContent>
            {teachers.map(t => (
              <SelectItem key={t.id} value={t.id}>{t.name?.toUpperCase()} - {t.instrument}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {teacherId && (
          <Button
            onClick={() => { setUploadFiles([]); setUploadCategory(''); setUploadDescription(''); setShowUploadModal(true); }}
            className="bg-[#0A0A0A] hover:bg-[#1a1a1a] rounded-sm"
            data-testid="upload-document-btn"
          >
            <UploadSimple size={18} className="mr-2" />Carica Documenti
          </Button>
        )}

        {teacherId && (
          <div className="bg-white border border-[#E4E4E7] rounded-sm px-4 py-1.5 text-sm ml-auto" data-testid="docs-count">
            <span className="text-[#52525B]">Documenti:</span> <span className="font-bold font-['Work_Sans']">{documents.length}</span>
          </div>
        )}
      </div>

      <div className="animate-fade-in animate-delay-2">
        {!teacherId ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
            <FolderOpen size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Seleziona un insegnante per visualizzare i documenti</p>
          </div>
        ) : loading ? (
          <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm">
            <FolderOpen size={48} className="mx-auto text-[#E4E4E7] mb-4" />
            <p className="text-[#52525B]">Nessun documento per {teacher?.name}</p>
            <p className="text-[#A1A1AA] text-sm mt-1">Usa "Carica Documenti" per aggiungere file</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full" data-testid="documents-table">
              <thead>
                <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">File</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Categoria</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Descrizione</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Dimensione</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => {
                  const dt = new Date(doc.uploaded_at);
                  const dateStr = dt.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' });
                  const timeStr = dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                  return (
                    <tr key={doc.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`doc-row-${doc.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          {fileIcon(doc.original_name)}
                          <span className="text-sm font-medium truncate max-w-[250px]">{doc.original_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {doc.category ? (
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${categoryColors[doc.category] || 'bg-gray-100 text-gray-600'}`}>
                            {doc.category}
                          </span>
                        ) : <span className="text-[#D4D4D8] text-sm">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-[#52525B] max-w-[200px] truncate">{doc.description || <span className="text-[#D4D4D8]">-</span>}</td>
                      <td className="px-4 py-2.5 text-sm text-right text-[#52525B] font-['Work_Sans']">{formatSize(doc.file_size)}</td>
                      <td className="px-4 py-2.5 text-xs text-[#52525B] whitespace-nowrap">
                        <div>{dateStr}</div>
                        <div className="text-[#A1A1AA]">{timeStr}</div>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleDownload(doc)}
                            className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                            title="Scarica"
                            data-testid={`download-doc-${doc.id}`}
                          >
                            <DownloadSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(doc.id)}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600"
                            title="Elimina"
                            data-testid={`delete-doc-${doc.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Carica Documenti per {teacher?.name}</DialogTitle>
            <DialogDescription>Seleziona uno o piu file da caricare</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Categoria</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="rounded-sm" data-testid="upload-category-select">
                  <SelectValue placeholder="Seleziona categoria (opzionale)" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Descrizione (opzionale)</Label>
              <Input
                value={uploadDescription}
                onChange={e => setUploadDescription(e.target.value)}
                placeholder="Es. Fattura dicembre 2025"
                className="rounded-sm"
                data-testid="upload-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="label-overline">File</Label>
              <Input
                type="file"
                multiple
                onChange={e => setUploadFiles(Array.from(e.target.files))}
                className="rounded-sm"
                data-testid="upload-files-input"
              />
              {uploadFiles.length > 0 && (
                <div className="space-y-1 mt-2">
                  {uploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-[#F4F4F5] px-3 py-1.5 rounded">
                      {fileIcon(f.name)}
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-[#A1A1AA] font-['Work_Sans']">{formatSize(f.size)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" disabled={uploading || !uploadFiles.length} className="bg-[#0A0A0A] rounded-sm" data-testid="upload-submit-btn">
                {uploading ? 'Caricamento...' : `Carica ${uploadFiles.length > 0 ? `(${uploadFiles.length})` : ''}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notify Teacher Modal */}
      <Dialog open={showNotifyModal} onOpenChange={setShowNotifyModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Notificare l'insegnante?</DialogTitle>
            <DialogDescription>Vuoi inviare un'email di notifica per i documenti appena caricati?</DialogDescription>
          </DialogHeader>
          {notifyData && (
            <div className="space-y-4">
              <div className="text-xs text-[#52525B] bg-[#F4F4F5] p-3 rounded-sm space-y-1">
                <div><strong>Destinatario:</strong> {notifyData.teacherName} ({notifyData.teacherEmail})</div>
              </div>
              <div className="border border-[#E4E4E7] rounded-sm overflow-hidden">
                <div className="bg-[#2D2A4A] text-white text-center py-3 text-sm font-semibold">Fiori Musicali</div>
                <div className="p-4 text-sm space-y-2">
                  <p>Gentile <strong>{notifyData.teacherName}</strong>,</p>
                  <p>sono stati caricati nuovi documenti nella tua area riservata:</p>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {notifyData.fileNames.map((fn, i) => (
                      <li key={i} className="text-sm">{fn}</li>
                    ))}
                  </ul>
                  {notifyData.category && <p><strong>Categoria:</strong> {notifyData.category}</p>}
                  {notifyData.description && <p><strong>Descrizione:</strong> {notifyData.description}</p>}
                  <p className="mt-3">Accedi alla piattaforma per visualizzarli e scaricarli.</p>
                  <p className="text-xs text-[#A1A1AA] mt-3">Cordiali saluti,<br />Segreteria Fiori Musicali</p>
                </div>
                <div className="bg-[#F4F4F5] text-center py-2 text-[10px] text-[#A1A1AA]">Fiori Musicali - Scuola di Musica</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNotifyModal(false); setNotifyData(null); }} className="rounded-sm" data-testid="notify-skip-btn">No, grazie</Button>
            <Button onClick={handleSendNotify} disabled={sendingNotify} className="bg-[#2D2A4A] hover:bg-[#1e1c38] rounded-sm" data-testid="notify-send-btn">
              {sendingNotify ? 'Invio...' : <><PaperPlaneTilt size={16} className="mr-1.5" /> Invia Notifica</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
